import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { FaArrowLeft } from "react-icons/fa";

const FAQ = () => {
  const navigate = useNavigate();
  usePageTitle("FAQ's");
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const faqs = [
    {
      question: "What is Datenite used for?",
      answer: "Datenite is a dating app designed to help you match, chat, and meet new people near your location. It offers smart matchmaking, video calling, and a unique date-publishing calendar to help users build real connections."
    },
    {
      question: "Is Datenite a free dating app?",
      answer: "Yes, Datenite is free to download and use. You can create a profile, swipe, match, and chat at no cost. Premium plans unlock extra features like boosts, unlimited swipes, and advanced filters."
    },
    {
      question: "Is Datenite safe for online dating?",
      answer: "Datenite is built with multiple safety layers including profile verification, AI monitoring, and in-app reporting tools. You can chat and video call without sharing personal information, keeping your experience secure."
    },
    {
      question: "How does Datenite match people?",
      answer: "Datenite uses interest-based and location-based matching to show you profiles that fit your preferences. The app learns from your swipes and improves recommendations over time."
    },
    {
      question: "Does Datenite work in my city?",
      answer: "Yes, Datenite is available across India and supports location-based matching in major cities and towns. Users can also filter for nearby connections."
    },
    {
      question: "How do I publish a date on Datenite?",
      answer: "You can publish a date by opening the Calendar tab, selecting a time and activity, and posting it for compatible users to view. This helps you plan real dates instead of endless chatting."
    },
    {
      question: "Can I video call someone on Datenite?",
      answer: "Yes, Datenite includes secure in-app video calling that lets you talk face-to-face without sharing your phone number. It's ideal for verifying profiles before meeting."
    },
    {
      question: "How do I block or report a user on Datenite?",
      answer: "Tap the three-dot menu on the user's profile or chat window and choose 'Report' or 'Block.' Datenite reviews reports 24/7 to keep the community safe."
    },
    {
      question: "Why am I not getting matches on Datenite?",
      answer: "Lack of matches usually happens due to narrow filters, low visibility, or incomplete profiles. Updating your photos, expanding preferences, or using boosts can improve your match rate."
    },
    {
      question: "How do I cancel my Datenite subscription?",
      answer: "You can cancel your subscription directly through the App Store or Google Play Store under your account's subscription settings. Turn off auto-renewal anytime to avoid future charges."
    },
    {
      question: "What is the 7–10 PM match boost on Datenite?",
      answer: "Datenite's 'Magic Hour' (7–10 PM) increases your profile visibility when most users are active. This improves your chances of getting more matches instantly."
    },
    {
      question: "Are Datenite profiles verified?",
      answer: "Yes, Datenite uses photo verification, AI checks, and manual moderation to ensure profiles are real. This reduces fake accounts and improves trust."
    },
    {
      question: "How does Datenite protect my data and privacy?",
      answer: "Datenite encrypts personal data and offers secure in-app communication to prevent information leaks. You control what you share and who sees your details."
    },
    {
      question: "Can I delete my Datenite account permanently?",
      answer: "Yes, you can permanently delete your account from the Settings section. All your matches, messages, and data will be removed from the app."
    },
    {
      question: "What makes Datenite different from other dating apps?",
      answer: "Datenite offers unique features like calendar-based date publishing, business listings, video calls, and daily match boosts. It focuses on meaningful connections rather than endless swiping."
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 flex-col">
              <div className="text-center lg:text-left">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-32 mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Find answers to common questions about using Datenite
            </p>
          </motion.div>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          className="container mx-auto px-4 pb-16 max-w-5xl"
        >
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index} 
                variants={itemVariants}
                className="bg-gray-900/80 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 md:p-8"
              >
                <h2 className="text-xl font-bold text-romantic-pink mb-3">
                  {index + 1}. {faq.question}
                </h2>
                <p className="text-gray-300">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
